import { useEffect } from 'react';
import { tabTriggerClass } from '~/components/ui/tabTriggerClass';
import { NavLink, Route, Routes, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ShippingRoutesTable } from './sections/ShippingRoutesTable';
import { AddOnCostsTable } from './sections/AddOnCostsTable';
import { PortsTable } from './sections/PortsTable';
import { DutyCostsTable } from './sections/DutyCostsTable';

const BASE = 'sea-freight';

const TABS = [
  {
    link: 'shipping-routes',
    labelKey: 'logistics.tabs.shipping_routes',
    component: <ShippingRoutesTable />,
  },
  {
    link: 'add-on-costs',
    labelKey: 'logistics.tabs.add_on_costs',
    component: <AddOnCostsTable />,
  },
  {
    link: 'ports',
    labelKey: 'logistics.tabs.ports',
    component: <PortsTable />,
  },
  {
    link: 'duty-costs',
    labelKey: 'logistics.tabs.duty_costs',
    component: <DutyCostsTable />,
  },
] as const;

// Always resolves to /sea-freight/<link> regardless of current sub-path.
function tabPath(pathname: string, link: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const idx = segments.lastIndexOf(BASE);
  const head =
    idx !== -1 ? '/' + segments.slice(0, idx + 1).join('/') : `/${BASE}`;
  return `${head}/${link}`;
}

export function Logistics() {
  const { t } = useTranslation('logistics');
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (pathname.split('/').filter(Boolean).pop() === BASE) {
      navigate(tabPath(pathname, 'shipping-routes'), { replace: true });
    }
  }, [pathname, navigate]);

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex items-center gap-6 px-1">
        <h1 className="text-xl font-semibold text-neut-prim whitespace-nowrap">
          {t('logistics.title')}
        </h1>
        <nav className="flex items-center gap-1 overflow-x-auto">
          {TABS.map((tab) => (
            <NavLink
              key={tab.link}
              to={tabPath(pathname, tab.link)}
              className={({ isActive }) => tabTriggerClass(isActive)}
            >
              {t(tab.labelKey)}
            </NavLink>
          ))}
        </nav>
      </div>

      <Routes>
        {TABS.map((tab) => (
          <Route
            key={tab.link}
            path={`${tab.link}/*`}
            element={tab.component}
          />
        ))}
      </Routes>
    </div>
  );
}
